import { Scissors, RotateCcw, Trash2, X, ArrowUpToLine, ArrowDownToLine, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { HelpButton } from './HelpButton';
import { FONT_FAMILIES } from './constants';

export function PropertiesPanel({
  selectedElement,
  selectedId,
  updateElement,
  deleteElement,
  startCutout,
  clearCutout,
  showHelp,
  showSpecTags,
  setShowSpecTags,
  showLegend,
  setShowLegend,
  specTagShowBox,
  setSpecTagShowBox,
  specTagOpacity,
  setSpecTagOpacity,
  specTagFontColor,
  setSpecTagFontColor,
  specTagBgColor,
  setSpecTagBgColor,
  specTagBorderColor,
  setSpecTagBorderColor,
  showWatermark,
  setShowWatermark,
  watermarkOpacity,
  setWatermarkOpacity,
  bringToFront,
  bringForward,
  sendBackward,
  sendToBack,
}) {
  if (!selectedElement) return null;

  return (
    <div className="w-64 bg-white border-l border-border flex flex-col max-h-[calc(100vh-140px)]" data-testid="properties-panel">
      <div className="p-3 border-b border-border flex items-center justify-between">
        <h3 className="font-heading text-sm font-medium">Properties</h3>
        {showHelp && (
          <HelpButton topic={selectedElement.element_type === 'text' ? 'text' : selectedElement.element_type === 'image' ? 'images' : 'shapes'} />
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3 text-sm">
      
      {/* Text Properties */}
      {selectedElement.element_type === 'text' && (
        <TextProperties 
          element={selectedElement}
          selectedId={selectedId}
          updateElement={updateElement}
        />
      )}
      
      {/* Image Properties */}
      {selectedElement.element_type === 'image' && (
        <ImageProperties 
          element={selectedElement}
          selectedId={selectedId}
          updateElement={updateElement}
          startCutout={startCutout}
          clearCutout={clearCutout}
        />
      )}
      
      {/* Shape properties */}
      {selectedElement.element_type === 'shape' && (
        <ShapeProperties 
          element={selectedElement}
          selectedId={selectedId}
          updateElement={updateElement}
        />
      )}
      
      {/* Common Properties - Opacity */}
      <div className="pt-4 border-t">
        <Label>Opacity</Label>
        <div className="flex items-center gap-3 mt-2">
          <Slider
            value={[(selectedElement.opacity !== undefined ? selectedElement.opacity : 1) * 100]}
            onValueChange={([val]) => updateElement(selectedId, { opacity: val / 100 })}
            max={100}
            min={0}
            step={1}
            className="flex-1"
          />
          <span className="text-sm text-muted-foreground w-12 text-right">
            {Math.round((selectedElement.opacity !== undefined ? selectedElement.opacity : 1) * 100)}%
          </span>
        </div>
      </div>
      
      {/* Shadow Controls */}
      <ShadowControls 
        element={selectedElement}
        selectedId={selectedId}
        updateElement={updateElement}
        showHelp={showHelp}
      />
      
      {/* Layer Order */}
      <div className="pt-4 border-t">
        <Label className="flex items-center gap-2">
          Layer Order
          {showHelp && <HelpButton topic="layers" />}
        </Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={() => bringToFront(selectedId)}>
            <ArrowUpToLine className="w-3 h-3 mr-1" /> Front
          </Button>
          <Button variant="outline" size="sm" onClick={() => sendToBack(selectedId)}>
            <ArrowDownToLine className="w-3 h-3 mr-1" /> Back
          </Button>
          <Button variant="outline" size="sm" onClick={() => bringForward(selectedId)}>
            <ArrowUp className="w-3 h-3 mr-1" /> Forward
          </Button>
          <Button variant="outline" size="sm" onClick={() => sendBackward(selectedId)}>
            <ArrowDown className="w-3 h-3 mr-1" /> Backward
          </Button>
        </div>
      </div>
      
      {/* Spec Tag Styling */}
      <SpecTagStyling 
        showHelp={showHelp}
        showSpecTags={showSpecTags}
        setShowSpecTags={setShowSpecTags}
        showLegend={showLegend}
        setShowLegend={setShowLegend}
        specTagShowBox={specTagShowBox}
        setSpecTagShowBox={setSpecTagShowBox}
        specTagOpacity={specTagOpacity}
        setSpecTagOpacity={setSpecTagOpacity}
        specTagFontColor={specTagFontColor}
        setSpecTagFontColor={setSpecTagFontColor}
        specTagBgColor={specTagBgColor}
        setSpecTagBgColor={setSpecTagBgColor}
        specTagBorderColor={specTagBorderColor}
        setSpecTagBorderColor={setSpecTagBorderColor}
      />
      
      {/* Watermark Settings */}
      <WatermarkSettings 
        showWatermark={showWatermark}
        setShowWatermark={setShowWatermark}
        watermarkOpacity={watermarkOpacity}
        setWatermarkOpacity={setWatermarkOpacity}
      />
      
      {/* Delete Button */}
      <div className="mt-4 pt-4 border-t">
        <Button
          variant="destructive"
          size="sm"
          className="w-full"
          onClick={() => deleteElement(selectedId)}
          data-testid="delete-element-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete Element
        </Button>
      </div>
      </div>
    </div>
  );
}

function TextProperties({ element, selectedId, updateElement }) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Text</Label>
        <Textarea
          value={element.text || ''}
          onChange={(e) => updateElement(selectedId, { text: e.target.value })}
          rows={3}
        />
      </div>
      
      <div>
        <Label>Font</Label>
        <Select
          value={element.font_family}
          onValueChange={(v) => updateElement(selectedId, { font_family: v })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FONT_FAMILIES.map(f => (
              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>Font Size</Label>
        <Input
          type="number"
          value={element.font_size || 16}
          onChange={(e) => updateElement(selectedId, { font_size: parseInt(e.target.value) || 16 })}
        />
      </div>
      
      <div>
        <Label>Color</Label>
        <Input
          type="color"
          value={element.text_color || '#1A1918'}
          onChange={(e) => updateElement(selectedId, { text_color: e.target.value })}
          className="h-10"
        />
      </div>
      
      <div className="flex gap-2">
        <Button
          variant={element.font_weight === 'bold' ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => updateElement(selectedId, { 
            font_weight: element.font_weight === 'bold' ? 'normal' : 'bold' 
          })}
        >
          B
        </Button>
        <Button
          variant={element.font_style === 'italic' ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => updateElement(selectedId, { 
            font_style: element.font_style === 'italic' ? 'normal' : 'italic' 
          })}
          className="italic"
        >
          I
        </Button>
      </div>
      
      {/* Text Alignment */}
      <div>
        <Label>Alignment</Label>
        <div className="flex gap-1 mt-1">
          <Button
            variant={(element.text_align || 'left') === 'left' ? 'secondary' : 'outline'}
            size="sm"
            className="flex-1"
            onClick={() => updateElement(selectedId, { text_align: 'left' })}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/>
            </svg>
          </Button>
          <Button
            variant={element.text_align === 'center' ? 'secondary' : 'outline'}
            size="sm"
            className="flex-1"
            onClick={() => updateElement(selectedId, { text_align: 'center' })}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/>
            </svg>
          </Button>
          <Button
            variant={element.text_align === 'right' ? 'secondary' : 'outline'}
            size="sm"
            className="flex-1"
            onClick={() => updateElement(selectedId, { text_align: 'right' })}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/>
            </svg>
          </Button>
        </div>
      </div>
      
      {/* Leader Line */}
      <LeaderLineControls 
        element={element}
        selectedId={selectedId}
        updateElement={updateElement}
      />
    </div>
  );
}

function LeaderLineControls({ element, selectedId, updateElement }) {
  return (
    <div className="pt-3 border-t">
      <Label className="flex items-center gap-2">
        Leader Line
        <Switch 
          checked={element.show_leader || false}
          onCheckedChange={(checked) => updateElement(selectedId, { 
            show_leader: checked,
            leader_end_x: element.leader_end_x || 0,
            leader_end_y: element.leader_end_y || 80,
            leader_curve_x: element.leader_curve_x || 0,
            leader_curve_y: element.leader_curve_y || 40
          })}
        />
      </Label>
      <p className="text-[10px] text-muted-foreground mt-1">
        Add an arrow pointing to items
      </p>
      
      {element.show_leader && (
        <div className="space-y-2 mt-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs w-16">Arrow</Label>
            <Switch 
              checked={element.show_arrow !== false}
              onCheckedChange={(checked) => updateElement(selectedId, { show_arrow: checked })}
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-xs w-16">Color</Label>
            <input
              type="color"
              value={element.leader_color || '#1A1918'}
              onChange={(e) => updateElement(selectedId, { leader_color: e.target.value })}
              className="w-8 h-6 rounded cursor-pointer border"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-xs w-16">Width</Label>
            <Slider
              value={[element.leader_width || 2]}
              onValueChange={([val]) => updateElement(selectedId, { leader_width: val })}
              max={8}
              min={1}
              step={1}
              className="flex-1"
            />
            <span className="text-xs w-4">{element.leader_width || 2}</span>
          </div>
          
          <p className="text-[10px] text-muted-foreground bg-muted/50 p-2 rounded">
            <strong>Drag handles to shape line:</strong><br/>
            <span className="text-green-500">●</span> Green = Start point<br/>
            <span className="text-blue-500">●</span> Blue = First curve<br/>
            <span className="text-purple-500">●</span> Purple = Second curve<br/>
            <span className="text-accent">●</span> Terracotta = Arrow tip<br/>
            <em className="text-[9px]">Use both curve points for S-curves</em>
          </p>
        </div>
      )}
    </div>
  );
}

function ImageProperties({ element, selectedId, updateElement, startCutout, clearCutout }) {
  return (
    <div className="space-y-4">
      {element.spec_num && (
        <div className="p-2 bg-accent/10 rounded border border-accent/30">
          <Label className="text-accent">SpecNum</Label>
          <p className="text-lg font-bold text-accent">{element.spec_num}</p>
        </div>
      )}
      
      {element.clip_title && (
        <div>
          <Label>Source</Label>
          <p className="text-sm text-muted-foreground">{element.clip_title}</p>
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Width</Label>
          <Input
            type="number"
            value={Math.round(element.width)}
            onChange={(e) => updateElement(selectedId, { width: parseInt(e.target.value) || 100 })}
          />
        </div>
        <div>
          <Label>Height</Label>
          <Input
            type="number"
            value={Math.round(element.height)}
            onChange={(e) => updateElement(selectedId, { height: parseInt(e.target.value) || 100 })}
          />
        </div>
      </div>
      
      <div>
        <Label>Rotation</Label>
        <Input
          type="number"
          value={Math.round(element.rotation || 0)}
          onChange={(e) => updateElement(selectedId, { rotation: parseInt(e.target.value) || 0 })}
        />
      </div>
      
      {/* Cutout controls */}
      <div className="pt-2 border-t">
        <Label>Background Removal</Label>
        <div className="flex gap-2 mt-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => startCutout(selectedId)}
            data-testid="cutout-btn"
          >
            <Scissors className="w-4 h-4 mr-1" />
            {element.crop_points ? 'Edit Cutout' : 'Cutout Item'}
          </Button>
          {element.crop_points && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => clearCutout(selectedId)}
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          )}
        </div>
        {element.crop_points && (
          <p className="text-xs text-muted-foreground mt-1">
            Cutout active ({element.crop_points.length} points)
          </p>
        )}
      </div>
    </div>
  );
}

function ShapeProperties({ element, selectedId, updateElement }) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Shape Type</Label>
        <p className="text-sm text-muted-foreground capitalize">{element.shape_type}</p>
      </div>
      
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Width</Label>
          <Input
            type="number"
            value={Math.round(element.width)}
            onChange={(e) => updateElement(selectedId, { width: parseInt(e.target.value) || 50 })}
          />
        </div>
        <div>
          <Label>Height</Label>
          <Input
            type="number"
            value={Math.round(element.height)}
            onChange={(e) => updateElement(selectedId, { height: parseInt(e.target.value) || 50 })}
          />
        </div>
      </div>
      
      {element.shape_type !== 'line' && (
        <div>
          <Label>Fill Color</Label>
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={element.fill_color || '#BC5D41'}
              onChange={(e) => updateElement(selectedId, { fill_color: e.target.value })}
              className="w-10 h-10 rounded cursor-pointer border border-border"
            />
            <Input
              value={element.fill_color || '#BC5D41'}
              onChange={(e) => updateElement(selectedId, { fill_color: e.target.value })}
              className="flex-1"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateElement(selectedId, { fill_color: null })}
              title="No fill"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      
      <div>
        <Label>Stroke Color</Label>
        <div className="flex gap-2 items-center">
          <input
            type="color"
            value={element.stroke_color || '#1A1918'}
            onChange={(e) => updateElement(selectedId, { stroke_color: e.target.value })}
            className="w-10 h-10 rounded cursor-pointer border border-border"
          />
          <Input
            value={element.stroke_color || '#1A1918'}
            onChange={(e) => updateElement(selectedId, { stroke_color: e.target.value })}
            className="flex-1"
          />
        </div>
      </div>
      
      <div>
        <Label>Stroke Width</Label>
        <Input
          type="number"
          value={element.stroke_width || 2}
          onChange={(e) => updateElement(selectedId, { stroke_width: parseInt(e.target.value) || 1 })}
          min={0}
          max={50}
        />
      </div>
      
      <div>
        <Label>Rotation</Label>
        <Input
          type="number"
          value={Math.round(element.rotation || 0)}
          onChange={(e) => updateElement(selectedId, { rotation: parseInt(e.target.value) || 0 })}
        />
      </div>
    </div>
  );
}

function ShadowControls({ element, selectedId, updateElement, showHelp }) {
  return (
    <div className="pt-4 border-t">
      <Label className="flex items-center gap-2">
        Shadow
        {showHelp && <HelpButton topic="shadows" />}
        <Switch 
          checked={element.shadow_enabled || false}
          onCheckedChange={(checked) => updateElement(selectedId, { 
            shadow_enabled: checked,
            shadow_blur: element.shadow_blur || 10,
            shadow_offset_x: element.shadow_offset_x || 3,
            shadow_offset_y: element.shadow_offset_y || 3,
            shadow_opacity: element.shadow_opacity || 0.3
          })}
        />
      </Label>
      {element.shadow_enabled && (
        <div className="space-y-3 mt-3">
          <div>
            <Label className="text-xs">Blur</Label>
            <div className="flex items-center gap-2">
              <Slider
                value={[element.shadow_blur || 10]}
                onValueChange={([val]) => updateElement(selectedId, { shadow_blur: val })}
                max={50}
                min={0}
                step={1}
                className="flex-1"
              />
              <span className="text-xs text-muted-foreground w-8">{element.shadow_blur || 10}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Offset X</Label>
              <Input
                type="number"
                value={element.shadow_offset_x || 3}
                onChange={(e) => updateElement(selectedId, { shadow_offset_x: parseInt(e.target.value) || 0 })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Offset Y</Label>
              <Input
                type="number"
                value={element.shadow_offset_y || 3}
                onChange={(e) => updateElement(selectedId, { shadow_offset_y: parseInt(e.target.value) || 0 })}
                className="h-8"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Shadow Opacity</Label>
            <div className="flex items-center gap-2">
              <Slider
                value={[(element.shadow_opacity || 0.3) * 100]}
                onValueChange={([val]) => updateElement(selectedId, { shadow_opacity: val / 100 })}
                max={100}
                min={0}
                step={5}
                className="flex-1"
              />
              <span className="text-xs text-muted-foreground w-10">{Math.round((element.shadow_opacity || 0.3) * 100)}%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SpecTagStyling({
  showHelp,
  showSpecTags,
  setShowSpecTags,
  showLegend,
  setShowLegend,
  specTagShowBox,
  setSpecTagShowBox,
  specTagOpacity,
  setSpecTagOpacity,
  specTagFontColor,
  setSpecTagFontColor,
  specTagBgColor,
  setSpecTagBgColor,
  specTagBorderColor,
  setSpecTagBorderColor,
}) {
  return (
    <div className="pt-4 border-t">
      <Label className="flex items-center gap-2">
        Spec Tag Styling
        {showHelp && <HelpButton topic="legend" />}
        <Badge variant="secondary" className="text-[10px]">Global</Badge>
      </Label>
      <div className="space-y-3 mt-3">
        <div className="flex items-center justify-between">
          <span className="text-sm">Show Tags</span>
          <Switch checked={showSpecTags} onCheckedChange={setShowSpecTags} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm">Show Legend</span>
          <Switch checked={showLegend} onCheckedChange={setShowLegend} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm">Show Box</span>
          <Switch checked={specTagShowBox} onCheckedChange={setSpecTagShowBox} />
        </div>
        <div>
          <Label className="text-xs">Tag Opacity</Label>
          <Slider
            value={[specTagOpacity * 100]}
            onValueChange={([val]) => setSpecTagOpacity(val / 100)}
            max={100}
            min={10}
            step={5}
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-[10px]">Font</Label>
            <input
              type="color"
              value={specTagFontColor}
              onChange={(e) => setSpecTagFontColor(e.target.value)}
              className="w-full h-8 rounded cursor-pointer border"
            />
          </div>
          <div>
            <Label className="text-[10px]">Background</Label>
            <input
              type="color"
              value={specTagBgColor}
              onChange={(e) => setSpecTagBgColor(e.target.value)}
              className="w-full h-8 rounded cursor-pointer border"
            />
          </div>
          <div>
            <Label className="text-[10px]">Border</Label>
            <input
              type="color"
              value={specTagBorderColor}
              onChange={(e) => setSpecTagBorderColor(e.target.value)}
              className="w-full h-8 rounded cursor-pointer border"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function WatermarkSettings({
  showWatermark,
  setShowWatermark,
  watermarkOpacity,
  setWatermarkOpacity,
}) {
  return (
    <div className="pt-4 border-t">
      <Label className="flex items-center gap-2">
        Logo Watermark
        <Badge variant="secondary" className="text-[10px]">Global</Badge>
      </Label>
      <div className="space-y-3 mt-3">
        <div className="flex items-center justify-between">
          <span className="text-sm">Show Watermark</span>
          <Switch checked={showWatermark} onCheckedChange={setShowWatermark} />
        </div>
        <div>
          <Label className="text-xs">Watermark Opacity</Label>
          <div className="flex items-center gap-2">
            <Slider
              value={[watermarkOpacity * 100]}
              onValueChange={([val]) => setWatermarkOpacity(val / 100)}
              max={50}
              min={5}
              step={5}
              className="flex-1"
            />
            <span className="text-xs text-muted-foreground w-10">
              {Math.round(watermarkOpacity * 100)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
